import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Chart, ChartData, registerables } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { DateTime } from 'luxon';

import { CHART_COLORS, WEATHER_COLORS } from '@app/admin/statistics/statistics.constants';
import { ChannelWeatherDto, DailyExposuresAndWeather, TotalsPerDay } from '@app/admin/statistics/statistics.model';
import { customPlugin } from '@app/shared/components/statistics/chart-weather-icons/labelIcon';
import { windArrowPlugin } from '@app/shared/components/statistics/chart-weather-icons/windArrow';
import { precipitationPlugin } from '@app/shared/components/statistics/chart-weather-icons/precipitation';
import { LuxonLanguageCodes } from '@app/core/translations';

@Component({
  selector: 'flow-totals-weather-per-day-chart',
  templateUrl: './totals-weather-per-day-chart.component.html',
  styleUrls: ['./totals-weather-per-day-chart.component.scss'],
})
export class TotalsWeatherPerDayChartComponent implements OnInit, OnChanges {
  @ViewChild(BaseChartDirective) chart: BaseChartDirective | undefined;
  @Input() startDate!: string;
  @Input() endDate!: string;
  @Input() dataPerDay: DailyExposuresAndWeather[];
  @Input() title: string;
  @Input() primaryLabel!: string;
  @Input() secondaryLabel!: string;
  @Input() barChartColor = CHART_COLORS.Primary;
  destroy$ = new Subject<void>();

  combinedLabels: Array<string | string[]> = [];
  barChartData: ChartData<'bar', number[], string | string[]> | null = null;
  barChartOptions = {
    responsive: true,
    scales: {
      x: {
        offset: true,
        type: 'category',
        beginAtZero: true,
        grid: {
          display: true,
          tickColor: 'white',
        },
        ticks: {
          beginAtZero: true,
          // padding widened to clear the icon + precipitation + wind-arrow rows drawn above the low/high + date labels
          padding: 62,
          align: 'center',
          autoSkip: true,
          autoSkipPadding: 20,
          maxRotation: 0,
          minRotation: 0,
        },
      },
      y: {
        type: 'linear',
        display: true,
        beginAtZero: true,
        title: {
          display: true,
          text: '',
        },
        grid: {
          display: false,
        },
      },
      y1: {
        type: 'linear',
        display: true,
        beginAtZero: false,
        position: 'right',
        min: -20,
        max: 30,
        title: {
          display: true,
          text: '',
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
    customPlugin: {
      enabled: true,
      images: ['../../../../../../assets/logo.png'],
    },
    plugins: {
      legend: {
        display: false,
        position: 'bottom',
      },
      tooltip: {
        callbacks: {
          label: context => {
            const dataset = context.chart.data.datasets[context.datasetIndex];
            return `${dataset.label}: ${context.parsed.y}`;
          },
        },
      },
    },
  }

  constructor(private translateService: TranslateService) {
    Chart.register(...registerables, customPlugin, windArrowPlugin, precipitationPlugin);
  }

  ngOnInit(): void {
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.chart) {
        this.chart.update();
        this.updateAxisTitles();
        this.setChartData(this.dataPerDay);
        this.setChartLabelTranslation();
      }
    });

    this.setChartLabelTranslation();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const dataPerDay = changes.dataPerDay?.currentValue;

    if (dataPerDay) {
      this.setChartData(dataPerDay);

      if (this.chart) {
        this.chart?.update();
      }
    }

  }

  setChartLabelTranslation() {
    this.barChartOptions.scales.y1.title.text = this.translateService.instant(this.secondaryLabel);
    this.barChartOptions.scales.y.title.text = this.translateService.instant(this.primaryLabel);
  }

  setChartData(dataPerDay: DailyExposuresAndWeather[]) {
    const weatherData = dataPerDay.map(d => d.channelWeatherDto);
    const peopleTotals = dataPerDay.map(d => d.totalsPerDay);
    const days = peopleTotals.map(d => DateTime.fromISO(d.day).toISODate());
    // low/high replaces the single average temperature on this chart; the stored average is
    // untouched elsewhere (still used for forecasting) — this is a presentation-only change.
    const lowHighLabels = weatherData.map(t => {
      const lowHigh =
        t.temperatureMin != null && t.temperatureMax != null
          ? { low: Math.round(t.temperatureMin), high: Math.round(t.temperatureMax) }
          : null;
      return lowHigh ? `${lowHigh.low}°/${lowHigh.high}°` : '';
    });
    const images = weatherData.map(i => `../../../../../../assets/png/${i.weather ? i.weather : 'no-icon'}.png`);

    this.combinedLabels = days.map((date, index) => {
      const lowHigh = lowHighLabels[index];
      return lowHigh ? [lowHigh, date] : ['-', date];
    });

    const barChartDataset = this.setBarChartData(peopleTotals);
    const lineChartDataset = this.setLineChartData(weatherData, images);

    this.barChartData = {
      labels: this.combinedLabels,
      datasets: [lineChartDataset, barChartDataset],
    };
  }

  setBarChartData(peopleTotals: TotalsPerDay[]) {
    const barValues = peopleTotals.length > 0 ? peopleTotals.map(d => d.totals) : [0];
    const barChartDataset = this.createDataSet(
      this.translateService.instant(this.primaryLabel),
      'bar',
      barValues,
      this.barChartColor,
      'y'
    );

    return barChartDataset;
  }

  setLineChartData(weatherData: ChannelWeatherDto[], images: string[]) {
    const lineValues = weatherData.map(t => t.temperature ? Math.round(t.temperature) : null);
    const lineChartDataset = this.createDataSet(
      this.translateService.instant('peopleCount.averageTemperature'),
      'line',
      lineValues,
      CHART_COLORS.SecondaryDark,
      'y1',
      images
    );

    (lineChartDataset as any).winds = weatherData.map(w => ({
      degree: w.windDegree,
      speed: w.windSpeed,
    }));

    (lineChartDataset as any).precipitation = weatherData.map(w => w.precipitation);
    (lineChartDataset as any).locale = LuxonLanguageCodes[this.translateService.currentLang] ?? 'en-US';

    return lineChartDataset;
  }

  createDataSet(label: string, type: string, data: string[] | number[], color: string, yAxisID: string, images?: string[]) {
    const dataset: any = {
      label,
      data,
      backgroundColor: color,
      borderColor: color,
      yAxisID,
      images,
    };

    if (type === 'line') {
      this.setLineChartDataset(dataset);
    } else {
      this.setBarChartDataset(dataset);
    }
    return dataset;
  }

  setLineChartDataset(dataset: any) {
    dataset.type = 'line';
    dataset.fill = false;
    dataset.tension = 0.3;
    dataset.pointRadius = 3;
    dataset.pointHoverBorderWidth = 1;
    dataset.pointBackgroundColor = (context: any) => this.getLineColor(context, dataset);
    dataset.pointBorderColor = (context: any) => this.getLineColor(context, dataset);
    dataset.borderColor = (context: any) => this.getLineColor(context, dataset);
    dataset.showLine = dataset.data?.length > 1;
  }

  getLineColor(context: any, dataset: any) {
    const chart = context.chart;
    const chartArea = chart?.chartArea;
    const ctx = chart?.ctx;

    if (!chartArea || !ctx) {
      return dataset.backgroundColor;
    }

    const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);

    gradient.addColorStop(0, WEATHER_COLORS.Cool);
    gradient.addColorStop(0.7, WEATHER_COLORS.Warm);
    gradient.addColorStop(1, WEATHER_COLORS.Hot);

    return gradient;
  }

  private updateAxisTitles(): void {
    if (this.barChartOptions?.scales) {
      this.barChartOptions.scales.y.title.text = this.translateService.instant('peopleCount.visitors');
      this.barChartOptions.scales.y1.title.text = this.translateService.instant('peopleCount.averageTemperature');
    }
  }

  setBarChartDataset(dataset: any) {
    dataset.type = 'bar';
    dataset.barPercentage = 1;
    dataset.hoverBackgroundColor = dataset.backgroundColor;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
